// =============================================================================
// selectors/tradeForm.ts — the trade form's arithmetic, re-exported for ui/.
// -----------------------------------------------------------------------------
// ⚠ `ui/` may not import `domain/` AT ALL, not even a type. Everything the modal
// renders comes through here.
//
// ⚠ Nothing is WRAPPED or re-scoped on the way through, and that is correct for
// this module specifically: every function below is Class W by construction — it
// computes one trade's own numbers from one trade's own fields. There is no
// population to get wrong, which is the thing `selectors/` normally exists to
// decide. A pass-through here is a deliberate answer to that question, not a
// missing one.
// =============================================================================

export {
  ANNOTATION_REQUIREMENTS,
  TRADE_FORM_TABS,
  convertRrField,
  deriveTrade,
  excursionDisplay,
  excursionRiskPoints,
  formatExcursionDuration,
  fromRiskPlan,
  fromStop,
  fromTarget,
  roundToTick,
  applyInitialRiskFlow,
  seedRiskPlan,
  seedRrField,
  seedSessionLabel,
  storedRrPlanned,
  defaultRiskFor,
  tabsNeedingAttention,
  unfilledRequirements,
  validateTradeForm,
} from '../domain/tradeForm';

export type {
  AnnotationRequirement,
  ExcursionDisplay,
  PlannedTargetUnit,
  TradeFormDerived,
  TradeFormTab,
  TradeFormValues,
} from '../domain/tradeForm';

/**
 * The discipline-breach flags, in the old client's `TRADE_BOOLEANS` order.
 *
 * ⚠ THE ORDER IS THE LAYOUT. The grid renders 2-up left→right, so this order is
 * what puts `Sub-A+ setup` beside `Plan not followed` and leaves
 * `controllableLoss` alone in the last slot — exactly Josh's Process screenshot.
 * Sorting it alphabetically would silently re-pair every row.
 *
 * ⚠ Every one is NEGATIVE: checked means something WENT WRONG. A user who reads
 * them as positives inverts their own discipline score and nothing on screen
 * looks off.
 */
export const TRADE_BOOLEANS: readonly {
  key: string; label: string; quality?: boolean; lossOnly?: boolean;
}[] = [
  // Setup QUALITY, not discipline — the old client excludes it from the
  // discipline grade, so it is flagged rather than grouped in.
  { key: 'subAPlus', label: 'Sub-A+ setup', quality: true },
  { key: 'planNotFollowed', label: 'Plan not followed' },
  { key: 'stopNotHonored', label: 'Stop not honored' },
  { key: 'targetNotHonored', label: 'Target not honored' },
  { key: 'revengeTrade', label: 'Revenge trade' },
  { key: 'fomoTrade', label: 'FOMO trade' },
  { key: 'overtraded', label: 'Overtraded' },
  // ⚠ LOSS-ONLY. Offering it on a winner invites a meaningless claim, and not
  // RENDERING it also means not sending it — so a flag stored on a trade that
  // later became a winner is preserved rather than cleared.
  { key: 'controllableLoss', label: 'Controllable loss', lossOnly: true },
];

/** `GRADES`, transcribed. */
export const GRADES = ['A+', 'A', 'B', 'C', 'D', 'F'] as const;

/** `SESSIONS`. The form auto-derives one from `timeIn`; this is the override list. */
export const SESSIONS = ['Asia', 'EU', 'NY-AM', 'NY-PM'] as const;

/**
 * Which session an entry time falls in — what "Auto" will actually pick.
 *
 * ⚠ A PASS-THROUGH, because `ui/` may not import `domain/` at all. The helper
 * has existed in `domain/session.ts` since the trade form was ported; it simply
 * had no route to a screen, so the Backtesting tab's SESSION dropdown said a
 * bare "Auto" and left the reader to guess which one that meant.
 */
export { sessionFromTime } from '../domain/session';
import { sessionFromTime as sessionFromTimeImpl } from '../domain/session';

/**
 * What the "Auto" option in a SESSION dropdown should read.
 *
 * `Auto (NY-AM)` once a time resolves, plain `Auto` when it does not — beta's
 * behaviour (app/index.html:31622), where the label refreshes as the entry time
 * is edited.
 *
 * ⚠ A FUNCTION RATHER THAN A TERNARY IN THE JSX, so the string a reader actually
 * sees is testable. The Backtesting tab has no test harness at all — it takes
 * nine providers — so inline this and the label is covered by nothing.
 *
 * ⚠ Never invents a session: an empty or unparseable time gives plain `Auto`,
 * because naming a session for a time nobody entered is worse than saying
 * nothing on the one control whose job is to report what it decided.
 */
export function autoSessionLabel(
  timeIn: string | null,
  date: string | null,
  tz?: string,
): string {
  const s = sessionFromTimeImpl(timeIn, date, tz);
  return s ? `Auto (${s})` : 'Auto';
}

/** `REGIMES`. */
export const REGIMES = ['Trending', 'Range', 'Choppy'] as const;
